/**
 * The Hiring → Probation reviews queue's filter + sort declaration — a consumer
 * of the generic filter toolkit (`components/filters`), mirroring
 * `studentsFilterConfig` (for the lookup facet) and `withdrawalsFilterConfig`
 * (for the sibling single-select pattern).
 *
 * Facets exposed:
 *   - verdict   → `verdict`   multi-select over the 5 probation verdicts
 *                             (pending / on_track / needs_improvement / at_risk /
 *                             terminate). `defaultValue: []` (no constraint = all)
 *                             with `allEncoding: 'omit'` (absent param = backend
 *                             returns every verdict) — replaces the old "All
 *                             verdicts" <select>.
 *   - teacherId → `teacherId` LOOKUP over `/api/teachers`. The URL carries only
 *                             the picked teacher id; the human label is resolved
 *                             + cached in the React layer. Mirrors the Students
 *                             page's Household lookup exactly (no status scoping,
 *                             so a terminated teacher still resolves by id).
 *
 * NOTE: `weekNo` stays a sibling single-select (NOT a toolkit facet). The
 * backend filters it as a SINGLE `1..12` value, so a comma-joined multi-select
 * would break it — the same reason `academicYear` stays a sibling on the
 * Withdrawals queue. The route feeds that select's value into the one query
 * alongside `apiParams` and declares it in the search schema so it round-trips
 * through the route boundary.
 *
 * Sorts map to the backend's allow-listed `sort` fields
 * ({weekNo, periodStart, createdAt, overallScore}); the default (first entry) is
 * "Week (earliest first)" = weekNo:asc — a stable, week-ordered reading of a
 * teacher's probation timeline. Both `weekNo` directions are registered so the
 * Week column header can toggle asc↔desc; overallScore / createdAt stay
 * dropdown-only single-direction sorts (like Withdrawals' amount / paidAt).
 */

import { z } from 'zod';
import { api } from '../../lib/api';
import type { TeacherRecord } from '../people/types';
import type { ListFilterConfig, SortColumnBinding } from '../filters';

const LOOKUP_PAGE_SIZE = 10;

/**
 * Search teachers by name / employee id → option rows for the Teacher lookup.
 * Deliberately UNSCOPED by status (mirrors the Household lookup, not the
 * active-only Batch lookup): probation reviews can attach to a teacher who has
 * since been terminated, and the picker must still be able to surface them.
 */
const loadTeacherOptions = async (
  query: string,
): Promise<{ value: string; label: string }[]> => {
  const res = await api.list<TeacherRecord>('/api/teachers', {
    q: query || undefined,
    pageSize: LOOKUP_PAGE_SIZE,
  });
  return res.items.map((t) => ({ value: t.id, label: t.name }));
};

/** Resolve a teacher id to its name (for hydrating a pill from a shared link). */
const resolveTeacherLabel = async (id: string): Promise<string | null> => {
  const teacher = await api
    .crud<TeacherRecord, never, never>('/api/teachers')
    .get(id);
  return teacher.name ?? null;
};

export const probationFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'multi-select',
      key: 'verdict',
      label: 'Verdict',
      group: 'filters',
      // Empty = "All verdicts"; `omit` drops the param so the backend returns
      // every verdict (the page's prior default).
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'on_track', label: 'On track' },
        { value: 'needs_improvement', label: 'Needs improvement' },
        { value: 'at_risk', label: 'At risk' },
        { value: 'terminate', label: 'Terminate' },
      ],
    },
    {
      kind: 'lookup',
      key: 'teacherId',
      label: 'Teacher',
      group: 'filters',
      searchNoun: 'teachers',
      loadOptions: loadTeacherOptions,
      resolveLabel: resolveTeacherLabel,
    },
  ],
  sorts: [
    { value: 'weekNo:asc', label: 'Week (earliest first)' },
    // Registered so the Week column header can toggle to descending; an
    // unregistered `field:dir` would silently fall back to the default sort.
    { value: 'weekNo:desc', label: 'Week (latest first)' },
    { value: 'overallScore:desc', label: 'Score (high → low)' },
    { value: 'createdAt:desc', label: 'Recently created' },
  ],
  resultNoun: ['review', 'reviews'],
};

/**
 * Column-header sort binding, synced to the same `sort` param. Only the Week
 * column is bound: it sorts on `weekNo`, the one field with both directions
 * registered above (overallScore / createdAt stay dropdown-only).
 */
export const PROBATION_SORT_COLUMNS: Record<'weekNo', SortColumnBinding> = {
  weekNo: { field: 'weekNo', firstClickDir: 'asc' },
};

/**
 * URL search-param contract for the Probation queue, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for the
 * facet + sort + page values; this schema validates the raw URL at the route
 * boundary (everything optional, since a bare `/hr/probation` opens on the
 * configured defaults).
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys. `weekNo` is the sibling single-select (NOT a toolkit
 * facet) and is declared here too, coerced + bounded to `1..12`, so it
 * round-trips through the route boundary. Kept beside the config (not in the
 * route) so this boundary is unit-testable without the route's React/router
 * side effects.
 */
export const probationSearchSchema = z.object({
  verdict: z.string().optional(),
  teacherId: z.string().optional(),
  weekNo: z.coerce.number().int().min(1).max(12).optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
